import bannerPlaceholder from "assets/images/evento-banner.jpg";
import { CameraOn, Check, Close, CloudUpload } from "assets/svg";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useEventStore, useGuestStore } from "store";
import * as SpacesService from "libs/digitalocean/SpacesService";
import { TPhotoChallenge, TPhotoChallengeDone } from "services/types";
import CdtAppPublicApi from "cdt-app-public-api/dist";
import { clarity } from "react-microsoft-clarity";
import { useNavigate } from "react-router-dom";
import TopBar from "pages/FeedV2/TopBar";
import { confettiShoot } from "./gamesUtils";
import GuestFormModal from "components/GuestFormModal";
import CreatePost from "pages/FeedV2/CreatePost";
import { useFeedStore } from "store";
import { TFeedEvent } from "cdt-app-public-api/dist/types";

export default function PhotoChallenge() {
  const { theme } = useEventStore();
  const [step, setStep] = useState<"intro" | "game">("intro");
  const navigate = useNavigate();
  const { event } = useEventStore();
  const { guest, setGameStatus } = useGuestStore();
  const [guestModalOpen, setGuestModalOpen] = useState(false);
  const [shouldStartAfterModal, setShouldStartAfterModal] = useState(false);

  const onPhotoChallengeClose = () => {
    if (event?.betaFeatures?.includes("feed")) {
      return navigate("/feed/v2");
    } else {
      navigate("/options");
    }
  };

  useEffect(() => {
    if (clarity.hasStarted()) {
      clarity.setTag("game_photo_challenge", "engaged");
    }
  }, []);

  const onStartClick = useCallback(() => {
    if (!event?.id || !guest?.id) return;

    if (!guest?.name) {
      setGuestModalOpen(true);
      setShouldStartAfterModal(true);
      return;
    }

    setGameStatus("photo_challenge_intro_seen", true);
    setStep("game");
  }, [event?.id, guest?.id, guest?.name, setGameStatus]);

  const handleGuestModalClose = useCallback(() => {
    setGuestModalOpen(false);

    if (shouldStartAfterModal) {
      setShouldStartAfterModal(false);
      setGameStatus("photo_challenge_intro_seen", true);
      setStep("game");
    }
  }, [shouldStartAfterModal, setGameStatus]);

  return (
    <div
      className={`${theme} w-full flex justify-center items-center px-5 py-10`}
    >
      <div className="w-full flex flex-col justify-start items-center">
        {/* Header */}
        <div className="flex w-full items-center justify-between mb-4 mt-6">
          <h1 className="m-0 p-0 grow text-center">
            <FormattedMessage
              id="title.photoChallenge"
              defaultMessage="Foto Desafio"
            />
          </h1>
          {event?.betaFeatures?.includes("feed") && (
            <TopBar onClose={onPhotoChallengeClose} />
          )}
          {step !== "intro" && !event?.betaFeatures?.includes("feed") && (
            <button
              className="flex items-center text-primary fixed right-3 top-3"
              onClick={onPhotoChallengeClose}
            >
              <span className="text-lg">
                <Close width={36} height={36} color="none" outline="#414141" />
              </span>
            </button>
          )}
        </div>
        {step === "intro" && <Intro onStartClick={onStartClick} />}
        {step === "game" && <Game />}
      </div>
      <GuestFormModal isOpen={guestModalOpen} onClose={handleGuestModalClose} />
    </div>
  );
}

const Intro = ({ onStartClick }: { onStartClick: () => void }) => {
  const { theme, event } = useEventStore();
  const [countdown, setCountdown] = useState(3);
  const [isDisabled, setIsDisabled] = useState(true);
  const { guest, getGameStatus } = useGuestStore();

  useEffect(() => {
    const hasSeenIntro = getGameStatus("photo_challenge_intro_seen");

    if (hasSeenIntro) {
      setIsDisabled(false);
      setCountdown(0);
      return;
    }

    if (countdown === 0) {
      setIsDisabled(false);

      return;
    }

    const timer = setTimeout(() => {
      setCountdown(countdown - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [countdown, guest?.id, getGameStatus]);

  return (
    <div className="flex flex-col justify-center items-center gap-4">
      {event?.customization?.theme.name === "rose" ? (
        <div className="relative wedding-rose">
          <img
            className="aspect-square object-cover w-full h-full"
            src={event?.bannerUrl || bannerPlaceholder}
            alt="banner-home"
          />
        </div>
      ) : (
        <div className="relative bg-white p-2 shadow-md">
          <img
            src={event?.bannerUrl || bannerPlaceholder}
            alt="event banner"
            className="aspect-[1/1.15] object-cover max-w-full"
          />
        </div>
      )}
      <p className="text-lg text-gray-500">
        <FormattedMessage
          id="message.photoChallenge.intro.new"
          defaultMessage="É fácil: siga as instruções e divirta-se tirando fotos!"
        />
      </p>
      <div className="w-full flex flex-col justify-center items-center gap-8">
        <button
          onClick={onStartClick}
          className={`px-10 py-3 ${theme.button}`}
          disabled={isDisabled}
        >
          <span className="text-center w-full">
            <FormattedMessage
              id="button.photoChallenge.start"
              defaultMessage="Iniciar"
            />
            {isDisabled && (
              <span className=" ml-2 animate-pulse">{countdown}</span>
            )}
          </span>
        </button>
      </div>
    </div>
  );
};

const Game = () => {
  const { event } = useEventStore();
  const {
    guest,
    getGameStatus,
    setGameStatus,
    games: { photoChallengesDone },
    addPhotoChallengeDone,
    updatePhotoChallengeDone,
    removePhotoChallengeDone,
  } = useGuestStore();
  const [
    photoChallengesDoneWithPublicUrl,
    setPhotoChallengeDonesWithPublicUrl,
  ] = useState<Record<string, TPhotoChallengeDone>>({});

  const guestId = useMemo(
    () => guest?.id || Math.random().toString(36).substring(2, 10),
    [guest]
  );

  const handlePhotoInput = useCallback(
    async (photoChallenge: TPhotoChallenge, photoFile: File) => {
      if (!event) return;

      try {
        const photoStoragePath = `photo-challenges/${event.id}/${guestId}/${photoChallenge.id}.jpg`;

        const newPhotoChallengeDone: TPhotoChallengeDone = {
          eventId: event.id as string,
          eventCode: event.code,
          uploadId: "",
          guestId,
          guestName: guest?.name as string,
          guestPhotoStoragePath: guest?.photoStoragePath || null,
          challengeId: photoChallenge.id,
          challengeText: photoChallenge.text,
          photoStoragePath,
          createdAt: new Date().toISOString(),
        };

        addPhotoChallengeDone(newPhotoChallengeDone);

        if (clarity.hasStarted()) {
          clarity.setTag("engagement_level_creator", "true");
        }

        await SpacesService.uploadFile(photoFile, photoStoragePath);

        const cdtAppPublicApi = new CdtAppPublicApi();

        const newUpload = {
          eventId: event.id as string,
          eventCode: event.code,
          fileStoragePath: photoStoragePath,
          fileName: photoFile.name,
          fileSize: photoFile.size,
          fileType: photoFile.type,
          fileExt: "jpg",
          guestId,
          guestName: guest?.name as string,
          guestPhotoStoragePath: guest?.photoStoragePath || null,
          tags: ["photo-challenge", photoChallenge.id, "release-now"],
        };

        const uploadResponse = await cdtAppPublicApi.createUpload(newUpload);

        if (!uploadResponse) {
          throw new Error("Upload failed");
        }

        newPhotoChallengeDone.uploadId = uploadResponse.id;

        const photoChallengeResultSaved =
          await cdtAppPublicApi.createPhotoChallenge(newPhotoChallengeDone);

        if (!photoChallengeResultSaved) {
          throw new Error("Photo challenge result failed");
        }

        updatePhotoChallengeDone(photoChallengeResultSaved);
      } catch (error) {
        console.error("Error handling photo confirmation: ", error);

        removePhotoChallengeDone(photoChallenge.id);
      }
    },
    [
      event,
      guest,
      guestId,
      addPhotoChallengeDone,
      updatePhotoChallengeDone,
      removePhotoChallengeDone,
    ]
  );

  useEffect(() => {
    const fetchPublicUrl = async () => {
      const results = await Promise.all(
        photoChallengesDone.map(async (result) => {
          if (result.photoUrl || !result.uploadId) return result;

          const photoUrl = await SpacesService.getFileUrl(
            result.photoStoragePath
          );

          return { ...result, photoUrl };
        })
      );

      const resultsMap = results.reduce((acc, result) => {
        acc[result.challengeId] = result;
        return acc;
      }, {} as Record<string, TPhotoChallengeDone>);

      setPhotoChallengeDonesWithPublicUrl(resultsMap);
    };

    fetchPublicUrl();
  }, [setPhotoChallengeDonesWithPublicUrl, photoChallengesDone]);

  const isCompleted = useMemo(() => {
    const totalChallenges = event?.photoChallenge?.length || 0;
    const completedChallenges = photoChallengesDone.length;

    if (totalChallenges === 0 || completedChallenges !== totalChallenges) {
      return false;
    }

    const allUploadsProcessed = photoChallengesDone.every(
      (challenge) =>
        photoChallengesDoneWithPublicUrl[challenge.challengeId]?.photoUrl
    );

    return allUploadsProcessed;
  }, [
    event?.photoChallenge?.length,
    photoChallengesDoneWithPublicUrl,
    photoChallengesDone,
  ]);

  useEffect(() => {
    if (isCompleted) {
      const hasSeenCongrats = getGameStatus("photo_challenge_congrats_seen");

      if (!hasSeenCongrats) {
        setGameStatus("photo_challenge_congrats_seen", true);
        setTimeout(() => confettiShoot(), 1000);
      }
    }
  }, [
    isCompleted,
    event?.photoChallenge?.length,
    getGameStatus,
    setGameStatus,
  ]);

  return (
    <div className="w-full flex flex-col items-start gap-8">
      <div className="w-full flex flex-col justify-start items-start gap-4">
        {event?.photoChallenge?.length !== photoChallengesDone.length && (
          <h2 className="text-xl font-bold">
            <FormattedMessage
              id="subtitle.photoChallenge.tasks"
              defaultMessage="Tarefas"
            />
          </h2>
        )}

        {isCompleted ? (
          <Result />
        ) : (
          <div className={`w-full pb-3`}>
            <span className="text-gray-500">
              <FormattedMessage
                id="message.photoChallenge.inProgress.new"
                defaultMessage="Selecione um desafio abaixo e envie sua foto."
              />
            </span>
          </div>
        )}
        <div className="w-full flex flex-col justify-start items-start gap-2">
          {event?.photoChallenge?.map((item) => {
            const result = photoChallengesDoneWithPublicUrl[item.id];

            if (result) {
              return <ChallengeDoneCard key={result.id} result={result} />;
            }

            return (
              <ChallengeToDoCard
                key={item.id}
                photoChallenge={item}
                onPhotoInput={handlePhotoInput}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

type ChallengeToDoProps = {
  photoChallenge: TPhotoChallenge;
  onPhotoInput: (photoChallenge: TPhotoChallenge, photoFile: File) => void;
};

const ChallengeToDoCard = (props: ChallengeToDoProps) => {
  const { photoChallenge, onPhotoInput } = props;
  const { theme } = useEventStore();
  const hiddenFileInput = useRef<HTMLInputElement>(null);

  const handleClick = () => {
    hiddenFileInput.current?.click();
  };

  const handleInputFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    e.preventDefault();
    const file = e.target.files?.[0];


    if (!file) return;


    onPhotoInput(photoChallenge, file);
  };

  return (
    <div
      className={`w-full flex gap-2 card min-h-24 ${theme.card} cursor-pointer`}
      onClick={handleClick}
    >
      <input
        type="file"
        accept="image/png,image/jpeg,image/jpg"
        className="hidden"
        ref={hiddenFileInput}
        onChange={handleInputFileChange}
      />

      <div className="w-full flex items-center gap-2 p-2 min-h-24">
        <div className="flex items-center justify-center border border-gray-300 rounded-xl aspect-square h-24 w-24 min-h-24 min-w-24 grow-0 shrink-0">
          <CameraOn width={36} height={36} color="none" outline="#000000" />
        </div>

        <span className="text-center text-gray-500 grow">
          {photoChallenge.text}
        </span>

        <div className="flex items-center grow-0 shrink-0 px-1">
          <span className="w-6 h-6 border-2 border-gray-300 rounded-xl"></span>
        </div>
      </div>
    </div>
  );
};

type ChallengeDoneProps = {
  result: TPhotoChallengeDone;
};

const ChallengeDoneCard = (props: ChallengeDoneProps) => {
  const { result } = props;
  const { theme } = useEventStore();

  return (
    <div key={result.id} className={`w-full card ${theme.card}`}>
      <div className="w-full flex items-center gap-2 p-2 min-h-24">
        {result.photoUrl ? (
          <div className="h-24 aspect-square relative overflow-hidden rounded-lg ml-auto grow-0 shrink-0">
            <img
              src={result.photoUrl}
              alt="Prova do desafio"
              className="absolute w-full h-full object-cover"
            />
          </div>
        ) : (
          <div className="p-2 animate-ping min-h-24 min-w-24 flex items-center justify-center grow-0 shrink-0">
            <CloudUpload
              width={24}
              height={24}
              color="none"
              outline="#656565"
            />
          </div>
        )}
        <span className="text-center text-gray-500 grow">
          {result.challengeText}
        </span>
        <div className="grow-0 shrink-0 px-1">
          <Check width={24} height={24} color="none" outline="#4CAF50" />
        </div>
      </div>
    </div>
  );
};

const Result = () => {
  const { event, theme } = useEventStore();
  const [createPost, setCreatePost] = useState(false);
  const { addOptimisticPost } = useFeedStore();
  const navigate = useNavigate();

  const handleClosePostCreation = () => {
    setCreatePost(false);
  };

  const handlePostCreation = (post: TFeedEvent) => {
    if (post && event?.code) {
      addOptimisticPost(event.code, post);
    }
    setCreatePost(false);
    if (event?.betaFeatures?.includes("feed")) {
      navigate("/feed/v2");
    } else {
      navigate("/options");
    }
  };

  const totalChallenges = event?.photoChallenge?.length || 0;

  return (
    <div className="flex flex-col justify-center items-center w-full">
      <p className="text-gray-500 mt-2 mb-8" onClick={confettiShoot}>
        <FormattedMessage
          id="message.photoChallenge.congrats"
          defaultMessage="🎉 Parabéns! 🎉"
        />
      </p>
      <p className="text-gray-500 text-center mb-8">
        <FormattedMessage
          id="message.photoChallenge.completed"
          defaultMessage="Você completou todos os {total} desafios de foto!"
          values={{ total: totalChallenges }}
        />
      </p>
      {event?.betaFeatures?.includes("feed") && (
        <button
          className={`px-8 py-3 mt-4 ${theme?.buttonSecondary}`}
          onClick={() => setCreatePost(true)}
        >
          <FormattedMessage
            id="button.photoChallenge.share"
            defaultMessage="Compartilhar Resultado"
          />
        </button>
      )}

      <CreatePost
        createPost={createPost}
        setPost={setCreatePost}
        onClose={handleClosePostCreation}
        onPostCreated={handlePostCreation}
        frameStyle="photo-challenge"
        gameTag="photo-challenge"
        gameResultText={`Completei todos os ${totalChallenges} desafios de foto! 📸✨`}
      />
    </div>
  );
};
